import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Phone, Navigation } from "lucide-react";
import { COUNTIES, PHONE_TEL } from "@/lib/content";

const EASE = [0.22, 1, 0.36, 1];

export default function ServiceArea() {
  const [active, setActive] = useState(COUNTIES[0]);

  return (
    <section id="service-area" data-testid="service-area-section" className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-32">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="mb-14 max-w-2xl"
      >
        <p className="font-mono-accent mb-4 text-xs uppercase tracking-[0.24em] text-amber-600">
          Service Area
        </p>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
          Serving South Florida's{" "}
          <span className="font-serif-accent font-medium italic text-slate-500">Atlantic Corridor</span>
        </h2>
        <p className="mt-4 text-base text-slate-600">
          Headquartered in Deerfield Beach, FL — dispatching across 6 coastal
          counties, from Hollywood to Cocoa Beach.
        </p>
      </motion.div>

      <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
        <div className="relative pl-10">
          <div aria-hidden className="absolute left-[13px] top-2 bottom-2 w-px bg-stone-300" />
          <div className="space-y-1.5">
            {COUNTIES.map((c, i) => {
              const isActive = active.id === c.id;
              return (
                <motion.button
                  key={c.id}
                  data-testid={`county-pill-${c.id}`}
                  onClick={() => setActive(c)}
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.07, ease: EASE }}
                  className="relative flex w-full items-center gap-4 rounded-2xl px-4 py-3.5 text-left transition-colors duration-200"
                >
                  <span
                    className={`absolute -left-[31px] h-3 w-3 rounded-full border-2 transition-all duration-300 ${
                      isActive ? "border-amber-600 bg-amber-500 scale-125" : "border-stone-300 bg-white"
                    }`}
                  />
                  <span
                    className={`font-display flex-1 text-base font-semibold transition-colors duration-200 sm:text-lg ${
                      isActive ? "text-slate-900" : "text-slate-400 hover:text-slate-600"
                    }`}
                  >
                    {c.name}
                  </span>
                  {isActive && (
                    <motion.span
                      layoutId="county-badge"
                      className="font-mono-accent rounded-full bg-amber-600/10 px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-amber-700"
                    >
                      {c.badge}
                    </motion.span>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            data-testid="county-detail-panel"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="rounded-[2rem] border border-stone-200 bg-white p-8 shadow-xl shadow-slate-900/5 sm:p-10"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-mono-accent text-[11px] uppercase tracking-[0.2em] text-amber-600">
                  {active.badge}
                </p>
                <h3 className="font-display mt-2 text-xl font-bold text-slate-900 sm:text-2xl">
                  {active.name}
                </h3>
              </div>
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-amber-500">
                <Navigation className="h-5 w-5" />
              </span>
            </div>

            <div className="mt-7 flex flex-wrap gap-2.5">
              {active.cities.map((city) => (
                <span
                  key={city}
                  data-testid={`city-tag-${city.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex items-center gap-1.5 rounded-full border border-stone-200 bg-[#FAF9F6] px-4 py-2 text-sm font-medium text-slate-700"
                >
                  <MapPin className="h-3.5 w-3.5 text-amber-600" />
                  {city}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-stone-200 pt-6">
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">{active.response}</span> available in
                this county.
              </p>
              <a
                data-testid="county-call-button"
                href={PHONE_TEL}
                className="flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-amber-600"
              >
                <Phone className="h-3.5 w-3.5" />
                Book This Zone
              </a>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
